import React, { useState, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, CardHeader, IconButton
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useTranslation } from 'react-i18next';
import TagEditor from '../Widgets/TagEditor';
import TagsDropdown from '../TagsDropdown';

const SettingDashboardSnapshot = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const tagEditorRef = useRef(null);

  // Unified action settings hook
  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'dashboardsnapshot',
    onSaveStatusChange,
    customComparison: (initial) => {
      return comment === (initial.comment || '');
    },
    customSaveData: () => ({
      comment: comment,
      useConditions: true,
      conditions
    }),
    loadDataFunction: (data) => {
      if (data.customData?.comment !== undefined) {
        setComment(data.customData.comment);
      }
    }
  });

  const handleTagClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleTagSelect = (tagName) => {
    if (tagEditorRef.current) {
      tagEditorRef.current.insertTag(tagName);
    }
    setAnchorEl(null);
  };

  return (
    <ActionSettingsLayout
      title={t('manualSnap.action_title', 'Dashboard Snapshot')}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
    >
      <Card sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #5757b010',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#5757b0',
                color: 'white',
                borderRadius: '6px',
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.7 },
                mr: { xs: 1, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
              }}>
                <DashboardIcon sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'middle' }} />
                SNAPSHOT
              </Box>
              {t('manualSnap.settings_title', 'Snapshot Settings')}
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fafbff',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2.5 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Typography sx={{ mb: 2, fontSize: '0.9rem', color: '#666' }}>
            {t('manualSnap.description', 'This action will automatically save a snapshot of all widgets in the dashboard when triggered. You can see these snapshots in the Dashboard view.')}
          </Typography>
          
          <Box sx={{ mb: 0 }}>
            <Typography sx={{
              fontWeight: 600,
              color: '#444',
              fontSize: '0.9rem',
              mb: 1,
            }}>
              {t('manualSnap.comment_label', 'Snapshot Comment')}
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <TagEditor
                ref={tagEditorRef}
                value={comment}
                onChange={setComment}
                placeholder={t('manualSnap.comment_placeholder', 'e.g. Snapshot created after initial document processing. You can use {tags} here.')}
                className="snapshot-comment-editor"
                tags={tags}
                onTagClick={handleTagClick}
              />
              <TagsDropdown
                tags={tags}
                handleTagSelect={handleTagSelect}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
              />
            </Box>
            <Typography sx={{
              fontSize: '0.75rem',
              color: '#666',
              mt: 1,
              fontStyle: 'italic',
            }}>
              {t('manualSnap.comment_help', 'The comment helps you identify the snapshot later. Tags like {filename} will be replaced.')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </ActionSettingsLayout>
  );
};

export default React.memo(SettingDashboardSnapshot);
